import React, { useState } from "react";
import Highcharts from "highcharts";
import HighchartsChart from "@/components/HighchartsChart";
import { format } from "date-fns";
import {
  FaChevronUp,
  FaChevronDown,
  FaPaperPlane,
  FaEye,
  FaReply,
  FaSync,
  FaTrashAlt,
  FaInfoCircle,
  FaExclamationTriangle,
  FaSpinner,
  FaMagic,
} from "react-icons/fa";
import { Dialog, DialogContent, DialogTitle } from "@/shedcnUI/dialog";

// Format ISO "2026-03-15" → "Mar 15"
const fmtDate = (iso: string) => {
  try {
    return format(new Date(iso), "MMM d");
  } catch {
    return iso;
  }
};
import ICONS from "@/assets/icons";
import { cn } from "@/utils/methods";
import { Drawer } from "@/shedcnUI/drawer";
import { sanitizeHtml } from "@/utils/sanitize";
import {
  useCampaignDetails,
  TrackerRow,
  ReplyDetail,
} from "../hooks/useCampaignDetails";
import { StatusChip } from "@/pages/NewEmailAccountPage/components/StatusChip";

interface CampaignDetailsDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  campaign: any | null;
  onViewProcess?: (campaign: any) => void;
}

// ── Small helpers ──────────────────────────────────────────────────────────────

const CampaignStatusChip = ({ status }: { status: string }) => {
  const s = status?.toLowerCase() ?? "";
  let color = "bg-gray-100 text-gray-600";
  let Icon: any = null;
  if (s === "completed" || s === "active" || s === "running") {
    color = "bg-green-light text-green-dark";
    Icon = ICONS.GreenCircleCheck;
  } else if (s === "deleted") {
    color = "bg-red-light text-red-dark";
    Icon = ICONS.RedCircleCheck;
  } else if (s === "processing" || s === "starting") {
    color = "bg-blue-50 text-blue-700";
  }
  return (
    <span
      className={cn(
        "flex items-center space-x-1.5 px-2.5 py-1.5 w-fit rounded-[6px] text-[12px] font-semibold",
        color,
      )}
    >
      {Icon && <Icon className="w-[10px] h-[10px]" />}
      <span className="capitalize leading-3">{status?.replace(/_/g, " ")}</span>
    </span>
  );
};

const statusChipColor = (status: string) => {
  switch (status?.toUpperCase()) {
    case "BOUNCED":
      return "bg-red-100 text-red-800";
    case "REPLIED":
      return "bg-green-200 text-green-800";
    case "CLICKED":
      return "bg-blue-100 text-blue-800";
    case "OPENED":
      return "bg-emerald-100 text-emerald-800";
    default:
      return "bg-gray-100 text-gray-700";
  }
};

function GreenCheck() {
  return (
    <svg width="16" height="16" viewBox="0 0 18 18" fill="none">
      <circle cx="9" cy="9" r="8.5" stroke="#00C54F" />
      <path
        d="M5.5 9l2.5 2.5 4.5-4.5"
        stroke="#00C54F"
        strokeWidth="1.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function RedCross() {
  return (
    <svg width="16" height="16" viewBox="0 0 18 18" fill="none">
      <circle cx="9" cy="9" r="8.5" stroke="#CA0005" />
      <path
        d="M6 6l6 6M12 6l-6 6"
        stroke="#CA0005"
        strokeWidth="1.2"
        strokeLinecap="round"
      />
    </svg>
  );
}

// ── Chart option builders ──────────────────────────────────────────────────────

const buildAreaChart = (
  dates: string[],
  opens: number[],
): Highcharts.Options => ({
  chart: {
    type: "area",
    backgroundColor: "transparent",
    height: 220,
    spacing: [10, 0, 10, 0],
  } as any,
  title: { text: undefined },
  xAxis: {
    categories: dates.length ? dates : ["No Data"],
    lineColor: "#E2E8F0",
    tickColor: "#E2E8F0",
    labels: {
      style: { color: "#94A3B8", fontSize: "10px" },
      formatter: function (this: any) {
        const last = this.axis.categories.length - 1;
        return this.pos === 0 || this.pos === last ? this.value : "";
      },
    },
  },
  yAxis: {
    title: { text: undefined },
    gridLineColor: "#E2E8F0",
    gridLineDashStyle: "Dash",
    min: 0,
    labels: { style: { color: "#94A3B8", fontSize: "10px" } },
  },
  legend: { enabled: false },
  credits: { enabled: false },
  tooltip: {
    backgroundColor: "#fff",
    borderColor: "#E2E8F0",
    borderRadius: 8,
    style: { fontSize: "11px" },
    shared: true,
  },
  plotOptions: {
    area: {
      fillColor: {
        linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
        stops: [
          [0, "rgba(0,110,255,0.18)"],
          [1, "rgba(0,110,255,0.0)"],
        ],
      },
      marker: {
        radius: 3,
        fillColor: "#FFFFFF",
        lineWidth: 2,
        lineColor: "#006EFF",
      },
      lineWidth: 2,
      threshold: null,
    },
  },
  series: [
    {
      type: "area",
      name: "Opened",
      color: "#006EFF",
      data: opens.length ? opens : [0],
    },
  ] as any,
});

const buildLineChart = (
  dates: string[],
  opens: number[],
  clicks: number[],
): Highcharts.Options => ({
  chart: {
    type: "spline",
    backgroundColor: "transparent",
    height: 170,
    spacing: [5, 0, 5, 0],
  } as any,
  title: { text: undefined },
  xAxis: {
    categories: dates.length ? dates : ["No Data"],
    lineColor: "transparent",
    tickLength: 0,
    labels: {
      style: { color: "#94A3B8", fontSize: "10px" },
      formatter: function (this: any) {
        const last = this.axis.categories.length - 1;
        return this.pos === 0 || this.pos === last ? this.value : "";
      },
    },
  },
  yAxis: {
    title: { text: undefined },
    gridLineColor: "#E2E8F0",
    min: 0,
    labels: { style: { color: "#94A3B8", fontSize: "10px" } },
  },
  legend: { enabled: false },
  credits: { enabled: false },
  tooltip: {
    backgroundColor: "#fff",
    borderColor: "#E2E8F0",
    borderRadius: 8,
    style: { fontSize: "11px" },
    shared: true,
  },
  plotOptions: { spline: { marker: { enabled: false }, lineWidth: 2 } },
  series: [
    {
      type: "spline",
      name: "Opens",
      color: "#F97316",
      data: opens.length ? opens : [0],
    },
    {
      type: "spline",
      name: "Clicks",
      color: "#FDBA74",
      data: clicks.length ? clicks : [0],
    },
  ] as any,
});

const buildDonutChart = (
  labels: string[],
  data: number[],
): Highcharts.Options => ({
  chart: { type: "pie", backgroundColor: "transparent", height: 220 } as any,
  title: { text: undefined },
  plotOptions: {
    pie: { innerSize: "60%", dataLabels: { enabled: false }, borderWidth: 0 },
  },
  legend: { enabled: false },
  credits: { enabled: false },
  tooltip: {
    backgroundColor: "#fff",
    borderColor: "#E2E8F0",
    borderRadius: 8,
    style: { fontSize: "11px" },
    pointFormat: "<b>{point.name}</b>: {point.y}",
  },
  series: [
    {
      type: "pie",
      name: "Devices",
      data: labels.map((label, idx) => ({
        name: label,
        y: data[idx] || 0,
        color: ["#FBCFE8", "#BAE6FD", "#FEF08A", "#CCFBF1"][idx % 4],
      })),
    },
  ] as any,
});

// ── Expanded tracker row ───────────────────────────────────────────────────────

interface TrackerRowExpandedProps {
  tracker: TrackerRow;
  campaignName: string;
  fetchTrackerReply: (
    id: number,
  ) => Promise<{ success: boolean; message: ReplyDetail | null }>;
  resendEmailMutation: any;
  sendReplyMutation: any;
  generateReplyMutation: any;
  generateFollowupMutation: any;
}

function TrackerRowExpanded({
  tracker,
  campaignName,
  fetchTrackerReply,
  resendEmailMutation,
  sendReplyMutation,
  generateReplyMutation,
  generateFollowupMutation,
}: TrackerRowExpandedProps) {
  const [replyContent, setReplyContent] = useState<
    ReplyDetail | null | "loading" | "error"
  >(null);
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [replyForm, setReplyForm] = useState({
    to: tracker.recipient_email,
    subject: tracker.is_replied ? "" : `Follow-up: ${campaignName}`,
    content: "",
  });

  // Open the compose form for a manual (non-AI) reply to a customer reply.
  const handleReplyDirectly = () => {
    if (
      tracker.is_replied &&
      replyContent &&
      replyContent !== "loading" &&
      replyContent !== "error"
    ) {
      setReplyForm((f) => ({
        ...f,
        to: (replyContent as ReplyDetail).from,
        subject: `Re: ${(replyContent as ReplyDetail).subject}`,
      }));
    }
    setShowReplyForm(true);
  };

  // Generate an AI reply to the customer's inbound reply, then let the user edit.
  const handleAIReply = async () => {
    setShowReplyForm(true);
    setGenerating(true);
    try {
      const res = await generateReplyMutation.mutateAsync({
        tracker_id: tracker.id,
      });
      const d = (res && res.data) || {};
      if (d.success && d.generated_reply) {
        setReplyForm((f) => ({
          ...f,
          to: d.to || f.to,
          subject: d.subject || f.subject,
          content: d.generated_reply,
        }));
      }
    } catch {
      /* hook surfaces a toast on error */
    } finally {
      setGenerating(false);
    }
  };

  // Generate an AI follow-up for a recipient who opened but did not reply.
  const handleSuggestFollowup = async () => {
    setShowReplyForm(true);
    setGenerating(true);
    try {
      const res = await generateFollowupMutation.mutateAsync({
        tracker_id: tracker.id,
      });
      const d = (res && res.data) || {};
      if (d.success && d.generated_followup) {
        setReplyForm((f) => ({
          ...f,
          to: d.to || tracker.recipient_email,
          subject: d.subject || `Follow-up: ${campaignName}`,
          content: d.generated_followup,
        }));
      }
    } catch {
      /* hook surfaces a toast on error */
    } finally {
      setGenerating(false);
    }
  };

  // Auto-load reply content when expanded for replied trackers
  React.useEffect(() => {
    if (!tracker.is_replied) return;
    setReplyContent("loading");
    fetchTrackerReply(tracker.id)
      .then((res) =>
        setReplyContent(res.success && res.message ? res.message : "error"),
      )
      .catch(() => setReplyContent("error"));
  }, [tracker.id]);

  const timestamps = [
    { label: "Opened", val: tracker.opened_at },
    { label: "Clicked", val: tracker.clicked_at },
    { label: "Replied", val: tracker.replied_at },
    { label: "Bounced", val: tracker.bounced_at },
  ];

  const clickedLinks = tracker.clicked_links
    ? Object.entries(tracker.clicked_links)
    : [];

  return (
    <div className="  pt-4 pl-1 flex gap-6">
      {/* Left: timestamps + action buttons */}
      <div className="w-[200px] shrink-0 flex flex-col gap-3 text-[12px]">
        {timestamps.map(({ label, val }) => (
          <div key={label} className="flex justify-between">
            <span className="text-text-primary">{label}</span>
            <span className="font-semibold text-primary-black">
              {val ? format(new Date(val), "dd MMM, hh:mm a") : "N/A"}
            </span>
          </div>
        ))}
        <div className="flex flex-wrap gap-2 mt-2">
          {tracker.is_replied ? (
            <>
              <button
                disabled={generating}
                onClick={(e) => {
                  e.stopPropagation();
                  handleAIReply();
                }}
                className="flex items-center gap-1.5 bg-brand-blue text-white px-[10px] py-[5px] rounded-full text-[12px] font-medium hover:bg-blue-600 transition-colors disabled:opacity-50"
              >
                {generating ? (
                  <FaSpinner className="animate-spin" size={11} />
                ) : (
                  <FaMagic size={11} />
                )}
                <span className="leading-3">
                  {generating ? "Generating…" : "AI Reply"}
                </span>
              </button>
              <button
                disabled={generating}
                onClick={(e) => {
                  e.stopPropagation();
                  handleReplyDirectly();
                }}
                className="flex items-center gap-1.5 bg-white border border-border-color text-primary-black px-3 py-1.5 rounded-[6px] text-[12px] font-medium hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                <FaPaperPlane size={10} />
                <span className="leading-3">Reply Directly</span>
              </button>
            </>
          ) : tracker.is_opened ? (
            <>
              <button
                disabled={generating}
                onClick={(e) => {
                  e.stopPropagation();
                  handleSuggestFollowup();
                }}
                className="flex items-center gap-1.5 bg-brand-blue text-white px-[10px] py-[5px] rounded-full text-[12px] font-medium hover:bg-blue-600 transition-colors disabled:opacity-50"
              >
                {generating ? (
                  <FaSpinner className="animate-spin" size={11} />
                ) : (
                  <FaMagic size={11} />
                )}
                <span className="leading-3">
                  {generating ? "Generating…" : "Suggest Follow-up"}
                </span>
              </button>
              <button
                disabled={generating}
                onClick={(e) => {
                  e.stopPropagation();
                  setReplyForm((f) => ({
                    ...f,
                    to: tracker.recipient_email,
                    subject: `Follow-up: ${campaignName}`,
                  }));
                  setShowReplyForm(true);
                }}
                className="flex items-center gap-1.5 bg-white border border-border-color text-primary-black px-3 py-1.5 rounded-[6px] text-[12px] font-medium hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                <FaPaperPlane size={10} />
                <span className="leading-3">Edit &amp; Send</span>
              </button>
              <button
                disabled={resendEmailMutation.isLoading}
                onClick={(e) => {
                  e.stopPropagation();
                  resendEmailMutation.mutate({
                    tracker_id: tracker.id,
                    recipient_email: tracker.recipient_email,
                  });
                }}
                className="flex items-center gap-1.5 bg-white border border-border-color text-primary-black px-3 py-1.5 rounded-[6px] text-[12px] font-medium hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                <ICONS.RefreshIcon className="w-4 h-4" />
                <span className="leading-3">Resend</span>
              </button>
            </>
          ) : (
            <button
              disabled={resendEmailMutation.isLoading}
              onClick={(e) => {
                e.stopPropagation();
                resendEmailMutation.mutate({
                  tracker_id: tracker.id,
                  recipient_email: tracker.recipient_email,
                });
              }}
              className="flex items-center gap-1.5 bg-brand-blue text-white px-[7px] py-[3px] rounded-full text-[12px] font-medium hover:bg-blue-600 transition-colors disabled:opacity-50"
            >
              <ICONS.RefreshIcon className="w-4 h-4 !text-white" />
              <span className="text-3 leading-3">Resend</span>
            </button>
          )}
        </div>

        {clickedLinks.length > 0 && (
          <div className="mt-4 pt-4 border-t border-border-color">
            <p className="text-[11px] text-text-primary font-medium mb-2 uppercase tracking-wider">
              Clicked Links
            </p>
            <div className="flex flex-col gap-2 max-h-[100px] overflow-y-auto custom-scrollbar">
              {clickedLinks.map(([url, count]) => (
                <div
                  key={url}
                  className="flex justify-between items-center bg-white border border-border-color rounded-[6px] px-2 py-1.5"
                >
                  <span
                    className="truncate text-brand-blue hover:underline cursor-pointer flex-1"
                    title={url}
                  >
                    {url}
                  </span>
                  <span className="ml-2 bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded text-[10px] font-bold">
                    {count}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Right: reply content preview OR compose form */}
      <div className="flex-1 bg-table-header-bg  border border-border-color rounded-[8px] p-4 text-[12px] text-primary-black leading-relaxed overflow-y-auto max-h-[160px] custom-scrollbar">
        {showReplyForm ? (
          <div
            className="flex flex-col gap-2"
            onClick={(e) => e.stopPropagation()}
          >
            <div>
              <label className="text-[11px] text-text-primary font-medium block mb-0.5">
                To
              </label>
              <input
                readOnly
                value={replyForm.to}
                className="w-full border border-border-color rounded-[6px] px-2 py-1 text-[12px] bg-gray-50 text-primary-black"
              />
            </div>
            <div>
              <label className="text-[11px] text-text-primary font-medium block mb-0.5">
                Subject
              </label>
              <input
                value={replyForm.subject}
                onChange={(e) =>
                  setReplyForm((f) => ({ ...f, subject: e.target.value }))
                }
                className="w-full border border-border-color rounded-[6px] px-2 py-1 text-[12px] text-primary-black focus:outline-none focus:ring-1 focus:ring-brand-blue"
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-0.5">
                <label className="text-[11px] text-text-primary font-medium block">
                  Message
                </label>
                {generating && (
                  <span className="flex items-center gap-1 text-[11px] text-brand-blue">
                    <FaSpinner className="animate-spin" size={9} /> Generating with AI…
                  </span>
                )}
              </div>
              <textarea
                rows={3}
                disabled={generating}
                value={replyForm.content}
                placeholder={generating ? "Drafting with AI…" : ""}
                onChange={(e) =>
                  setReplyForm((f) => ({ ...f, content: e.target.value }))
                }
                className="w-full border border-border-color rounded-[6px] px-2 py-1 text-[12px] text-primary-black resize-none focus:outline-none focus:ring-1 focus:ring-brand-blue disabled:bg-gray-50"
              />
            </div>
            <div className="flex gap-2">
              <button
                disabled={
                  sendReplyMutation.isLoading || !replyForm.content.trim()
                }
                onClick={() =>
                  sendReplyMutation.mutate(
                    {
                      tracker_id: tracker.id,
                      to: replyForm.to,
                      subject: replyForm.subject,
                      content: replyForm.content,
                    },
                    { onSuccess: () => setShowReplyForm(false) },
                  )
                }
                className="flex items-center gap-1.5 bg-brand-blue text-white px-3 py-1.5 rounded-[6px] text-[12px] font-medium hover:bg-blue-600 disabled:opacity-50"
              >
                <FaPaperPlane size={10} />{" "}
                {sendReplyMutation.isLoading ? "Sending…" : "Send"}
              </button>
              <button
                onClick={() => setShowReplyForm(false)}
                className="flex items-center gap-1.5 bg-white border border-border-color text-primary-black px-3 py-1.5 rounded-[6px] text-[12px] font-medium hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : tracker.is_replied ? (
          <>
            {replyContent === "loading" && (
              <div className="flex items-center gap-2 text-text-primary">
                <div className="w-4 h-4 rounded-full border-2 border-brand-blue border-t-transparent animate-spin shrink-0" />
                Loading reply…
              </div>
            )}
            {replyContent === "error" && (
              <p className="text-text-primary italic mt-2">
                Reply not available yet. It may not have synced.
              </p>
            )}
            {replyContent &&
              replyContent !== "loading" &&
              replyContent !== "error" && (
                <div
                  dangerouslySetInnerHTML={{
                    __html: sanitizeHtml(
                      (replyContent as ReplyDetail).html_content || "",
                    ),
                  }}
                />
              )}
          </>
        ) : (
          <p className="text-text-primary italic mt-2">
            No message preview available.
          </p>
        )}
      </div>
    </div>
  );
}

// ── Main drawer ────────────────────────────────────────────────────────────────

export default function CampaignDetailsDrawer({
  open,
  onOpenChange,
  campaign,
  onViewProcess,
}: CampaignDetailsDrawerProps) {
  const [expandedRowId, setExpandedRowId] = useState<number | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [trackerToResend, setTrackerToResend] = useState<TrackerRow | null>(null);
  const [resendingId, setResendingId] = useState<number | null>(null);

  const {
    stats,
    trackers,
    chartData,
    deviceData,
    isLoading,
    refetchDetail,
    refreshStatsMutation,
    deleteMutation,
    resendEmailMutation,
    sendReplyMutation,
    generateReplyMutation,
    generateFollowupMutation,
    checkReplyMutation,
    fetchTrackerReply,
  } = useCampaignDetails(open ? (campaign?.uuid ?? null) : null);

  const handleResendClick = (tracker: TrackerRow) => {
    setTrackerToResend(tracker);
  };

  const confirmResend = () => {
    if (!trackerToResend || resendEmailMutation.isLoading) return;
    
    const id = trackerToResend.id;
    const email = trackerToResend.recipient_email;

    setResendingId(id);
    setTrackerToResend(null); // Close dialog immediately

    resendEmailMutation.mutate({
      tracker_id: id,
      recipient_email: email,
    }, {
      onSettled: () => {
        setResendingId(null);
      }
    });
  };

  if (!campaign) return null;

  const metricCards = [
    { label: "Sent", value: stats?.total_sent ?? 0, rate: null, active: false },
    {
      label: "Opened",
      value: stats?.total_opened ?? 0,
      rate: stats?.open_rate ?? null,
      active: false,
    },
    {
      label: "Clicked",
      value: stats?.total_clicked ?? 0,
      rate: stats?.click_rate ?? null,
      active: false,
    },
    {
      label: "Replied",
      value: stats?.total_replied ?? 0,
      rate: stats?.reply_rate ?? null,
      active: false,
    },
    {
      label: "Bounced",
      value: stats?.total_bounced ?? 0,
      rate: stats?.bounce_rate ?? null,
      active: false,
    },
  ];

  const formattedDates = chartData.dates.map(fmtDate);
  const dateRangeStr =
    formattedDates.length > 0
      ? `${formattedDates[0]} – ${formattedDates[formattedDates.length - 1]}`
      : "All time";

  const DateBadge = ({ small }: { small?: boolean }) => (
    <div
      className={cn(
        "flex items-center gap-1.5 rounded-[10px] border border-border-color bg-white text-primary-black",
        small
          ? "text-[11px] px-[8px] py-[6px]"
          : "text-[13px] px-[10px] py-[8px]",
      )}
    >
      <ICONS.RefreshIcon className="w-3 h-3" /> {dateRangeStr}
    </div>
  );

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      width="50%"
      className="rounded-none"
    >
      <div className="space-y-4 px-1 py-2 max-h-screen overflow-y-auto custom-scrollbar relative">
        {isLoading && (
          <div className="absolute inset-0 bg-white/60 z-50 flex items-center justify-center pointer-events-none rounded-[15px]">
            <div className="w-8 h-8 rounded-full border-4 border-brand-blue border-t-transparent animate-spin" />
          </div>
        )}

        {/* ── Header ── */}
        <div className="flex flex-col items-center gap-3 pt-0 pb-2">
          <h2 className="font-semibold text-[24px] text-primary-black text-center">
            {campaign.name}
          </h2>
          <div className="flex items-center gap-2">
            <span className="text-base text-light-black">
              Created on:{" "}
              {campaign.created_at
                ? format(new Date(campaign.created_at), "d MMMM, yyyy")
                : "—"}
            </span>
            <CampaignStatusChip status={campaign.status} />
          </div>
          <div className="flex items-center rounded-[8px] border border-border-color bg-white overflow-hidden px-[10px] py-[1px]">
            <button
              onClick={() => {
                refreshStatsMutation.mutate();
                refetchDetail();
              }}
              disabled={refreshStatsMutation.isLoading}
              className="flex items-center gap-1.5 mr-3 text-[13px] font-medium text-primary-black transition-colors disabled:opacity-50 leading-6"
            >
              <ICONS.RefreshIcon
                className={cn(
                  "w-4 h-4",
                  refreshStatsMutation.isLoading && "animate-spin",
                )}
              />{" "}
              Refresh
            </button>
            <div className="w-px h-8 bg-slate-200 shrink-0" />
            <button
              disabled={deleteMutation.isLoading}
              onClick={() => setIsDeleteModalOpen(true)}
              className="flex items-center gap-1.5 ml-3 text-[13px] font-medium text-red-600 hover:text-red-800 transition-colors disabled:opacity-50 leading-6"
            >
              <ICONS.DisableIcon className="w-4 h-4" /> Delete
            </button>
          </div>
        </div>

        {/* ── Running banner ── */}
        <div className="rounded-[14px] border border-border-color px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ICONS.YellowFlaim className="w-5 h-5 shrink-0" />
            <span className="text-lg font-bold text-primary-black">
              {campaign.sent_count}/{campaign.emails_count}
            </span>
            <CampaignStatusChip status={campaign.status} />
          </div>
          <button
            type="button"
            onClick={() => {
              if (onViewProcess) {
                onViewProcess(campaign);
                return;
              }

              const taskId = campaign?.latest_sending_task_id;
              if (taskId) {
                // Fallback: open the legacy status page if parent didn't wire a drawer
                window.location.href = `/email/sending-status/${taskId}`;
              }
            }}
            className="text-primary-black text-[14px] font-semibold hover:underline transition"
          >
            View Process
          </button>
        </div>

        {/* ── Analytics Overview ── */}
        <div className="rounded-[20px] border border-border-color bg-white p-5">
          <div className="flex items-center justify-between mb-5">
            <p className="text-base font-medium text-primary-black">
              Analytics Overview
            </p>
            <DateBadge />
          </div>

          {/* 5 mini metric cards */}
          <div className="grid grid-cols-5 gap-3 mb-6">
            {isLoading
              ? [1, 2, 3, 4, 5].map((i) => (
                  <div
                    key={i}
                    className="rounded-[10px] border border-border-color p-3 animate-pulse"
                  >
                    <div className="h-2.5 bg-slate-100 rounded w-2/3 mb-3" />
                    <div className="h-6 bg-slate-100 rounded w-1/2" />
                  </div>
                ))
              : metricCards.map((card) => (
                  <div
                    key={card.label}
                    className={cn(
                      "rounded-[10px] border p-3 pt-4 relative",
                      card.active
                        ? "border-brand-blue bg-[#F8FAFC]"
                        : "border-border-color bg-white",
                    )}
                  >
                    {card.active && (
                      <div className="absolute top-2 right-2 w-3 h-3 rounded-full bg-brand-blue" />
                    )}
                    <p className="text-[13px] text-text-primary mb-1">
                      {card.label}
                    </p>
                    <p className="mt-2 text-[18px] font-semibold text-primary-black flex items-center gap-0.5">
                      <ICONS.EmailAt className="w-[15px] h-[15px] text-text-primary" />
                      {card.value}
                    </p>
                    {/* {card.rate !== null && (
                                        <p className="text-[11px] text-text-primary mt-0.5">{card.rate.toFixed(1)}%</p>
                                    )} */}
                  </div>
                ))}
          </div>

          <HighchartsChart
            options={buildAreaChart(formattedDates, chartData.opens)}
          />
        </div>

        {/* ── Engagement + Device ── */}
        <div className="grid grid-cols-2 gap-4">
          <div className="rounded-[20px] border border-border-color bg-white p-5">
            <div className="flex items-center justify-between mb-4">
              <p className="text-base font-medium text-primary-black">
                Engagement Over Time
              </p>
              <DateBadge small />
            </div>
            <div className="flex items-center gap-8 mb-4">
              <div>
                <p className="text-[22px] font-semibold text-primary-black leading-none">
                  {stats?.total_opened ?? 0}
                </p>
                <div className="flex items-center gap-1.5 mt-2">
                  <span className="w-2 h-2 rounded-full bg-orange-500 inline-block shrink-0" />
                  <span className="text-[13px] text-text-primary">Opens</span>
                </div>
              </div>
              <div>
                <p className="text-[22px] font-semibold text-primary-black leading-none">
                  {stats?.click_rate != null
                    ? `${stats.click_rate.toFixed(1)}%`
                    : "0%"}
                </p>
                <div className="flex items-center gap-1.5 mt-2">
                  <span className="w-2 h-2 rounded-full bg-orange-200 inline-block shrink-0" />
                  <span className="text-[13px] text-text-primary">Clicks</span>
                </div>
              </div>
            </div>
            <HighchartsChart
              options={buildLineChart(
                formattedDates,
                chartData.opens,
                chartData.clicks,
              )}
            />
          </div>

          <div className="rounded-[20px] border border-border-color bg-white p-5">
            <div className="flex items-center justify-between mb-4">
              <p className="text-base font-medium text-primary-black">
                Device Distribution
              </p>
              <DateBadge small />
            </div>
            <div className="flex flex-wrap gap-x-3 gap-y-2 mb-1 text-[10px] text-text-primary">
              {[
                { name: "Android", color: "#FBCFE8" },
                { name: "Desktop", color: "#BAE6FD" },
                { name: "Tablet", color: "#FEF08A" },
                { name: "Mobile", color: "#CCFBF1" },
              ].map((d) => (
                <div key={d.name} className="flex items-center gap-1">
                  <span
                    className="w-5 h-2 rounded-sm"
                    style={{ backgroundColor: d.color }}
                  />
                  {d.name}
                </div>
              ))}
            </div>
            <HighchartsChart
              options={buildDonutChart(deviceData.labels, deviceData.data)}
            />
          </div>
        </div>

        {/* ── Detailed Statistics ── */}
        <div className="rounded-[20px] border border-border-color bg-white p-5 overflow-hidden">
          <div className="flex items-center justify-between mb-5">
            <p className="text-base font-medium text-primary-black">
              Detailed Statistics
            </p>
            <DateBadge />
          </div>

          {trackers.length === 0 && !isLoading ? (
            <div className="flex items-center justify-center py-10 text-text-primary text-sm">
              No tracking data yet.
            </div>
          ) : (
            <div className="-mx-4 px-4 overflow-x-auto relative">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-table-header-bg">
                    <th className="p-4 text-[13px] text-text-primary font-medium leading-[1.25] rounded-tl-[12px] rounded-bl-[12px] text-left">
                      Recipient
                    </th>
                    <th className="p-4 text-[13px] font-medium text-text-primary text-left">
                      Status
                    </th>
                    <th className="p-4 text-[13px] font-medium text-text-primary text-center">
                      Opened
                    </th>
                    <th className="p-4 text-[13px] font-medium text-text-primary text-center">
                      Clicked
                    </th>
                    <th className="p-4 text-[13px] font-medium text-text-primary text-center">
                      Replied
                    </th>
                    <th className="p-4 text-[13px] text-text-primary font-medium text-center">
                      Bounced
                    </th>
                    <th className="p-4 text-[13px] text-text-primary font-medium rounded-tr-[12px] rounded-br-[12px] text-center">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {trackers.map((tracker) => {
                    const isExpanded = expandedRowId === tracker.id;
                    return (
                      <React.Fragment key={tracker.id}>
                        <tr
                          className="hover:bg-row-hover-bg transition-colors cursor-pointer border-b border-border-color last:border-b-0"
                          onClick={() =>
                            setExpandedRowId(isExpanded ? null : tracker.id)
                          }
                        >
                          <td className="p-4 font-medium text-primary-black">
                            <div className="flex items-center gap-2">
                              <span className="text-text-primary text-[10px]">
                                {isExpanded ? (
                                  <FaChevronUp />
                                ) : (
                                  <FaChevronDown />
                                )}
                              </span>
                              <span className="truncate max-w-[160px] text-[13px]">
                                {tracker.recipient_email}
                              </span>
                            </div>
                          </td>
                          <td className="p-4">
                            {/* <span className={cn('px-2 py-0.5 rounded-[4px] text-[11px] font-semibold', statusChipColor(tracker.status))}>
                                                        {tracker.status}
                                                    </span> */}
                            <StatusChip status={tracker.status} />
                          </td>
                          <td className="p-4 text-center">
                            <span className="flex justify-center">
                              {tracker.is_opened ? (
                                <GreenCheck />
                              ) : (
                                <RedCross />
                              )}
                            </span>
                          </td>
                          <td className="p-4 text-center">
                            <div className="flex flex-col items-center justify-center gap-0.5">
                              {tracker.is_clicked ? (
                                <GreenCheck />
                              ) : (
                                <RedCross />
                              )}
                              {tracker.click_count > 0 && (
                                <span className="text-[10px] font-bold text-blue-600">
                                  {tracker.click_count}{" "}
                                  {tracker.click_count === 1
                                    ? "click"
                                    : "clicks"}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="p-4 text-center">
                            <span className="flex justify-center">
                              {tracker.is_replied ? (
                                <GreenCheck />
                              ) : (
                                <RedCross />
                              )}
                            </span>
                          </td>
                          <td className="p-4 text-center">
                            <span className="flex justify-center">
                              {tracker.is_bounced ? (
                                <GreenCheck />
                              ) : (
                                <RedCross />
                              )}
                            </span>
                          </td>
                          <td className="p-4 text-center">
                            <div className="flex justify-center gap-2">
                              {tracker.is_bounced ? (
                                <span className="text-[11px] text-text-primary italic">
                                  N/A
                                </span>
                              ) : tracker.is_replied ? (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setExpandedRowId(
                                      isExpanded ? null : tracker.id,
                                    );
                                  }}
                                  className="flex items-center gap-1 bg-blue-50 text-blue-700 px-2.5 py-1 rounded-md text-[11px] font-medium hover:bg-blue-100 transition-colors"
                                >
                                  <FaEye size={10} /> View
                                </button>
                              ) : tracker.is_opened ? (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setExpandedRowId(
                                      tracker.id,
                                    ); /* Could also pre-trigger follow-up mode here */
                                  }}
                                  className="flex items-center gap-1 bg-green-50 text-green-700 px-2.5 py-1 rounded-md text-[11px] font-medium hover:bg-green-100 transition-colors"
                                >
                                  <FaReply size={10} /> Follow Up
                                </button>
                              ) : (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleResendClick(tracker);
                                  }}
                                  disabled={resendingId !== null}
                                  className="flex items-center gap-1 bg-orange-50 text-orange-700 px-2.5 py-1 rounded-md text-[11px] font-medium hover:bg-orange-100 transition-colors disabled:opacity-50"
                                >
                                  <FaSync
                                    size={10}
                                    className={
                                      resendingId === tracker.id
                                        ? "animate-spin"
                                        : ""
                                    }
                                  />{" "}
                                  Resend
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr>
                            <td colSpan={6} className="px-4 pb-4">
                              <TrackerRowExpanded
                                tracker={tracker}
                                campaignName={campaign.name}
                                fetchTrackerReply={fetchTrackerReply}
                                resendEmailMutation={resendEmailMutation}
                                sendReplyMutation={sendReplyMutation}
                                generateReplyMutation={generateReplyMutation}
                                generateFollowupMutation={generateFollowupMutation}
                              />
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="pb-6" />
      </div>

      <Dialog open={isDeleteModalOpen} onOpenChange={setIsDeleteModalOpen}>
        <DialogContent
          className="max-w-md p-0 overflow-hidden border-none shadow-2xl"
          onClose={setIsDeleteModalOpen}
        >
          <div className="p-0">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-red-50 rounded-2xl flex items-center justify-center">
                <FaTrashAlt className="text-red-500 text-base" />
              </div>
              <div className="mb-0">
                <DialogTitle className="text-xl font-semibold text-[#17150b] leading-tight">
                  Delete Campaign?
                </DialogTitle>
              </div>
            </div>

            <div className="space-y-4">
              <p className="text-[#7d7b78] leading-relaxed">
                You are about to delete{" "}
                <span className="font-bold text-[#17150b]">
                  {campaign.name}
                </span>
                . This action will permanently remove all emails, settings, and
                analytics associated with this campaign.
              </p>

              <div className="bg-amber-50 border border-amber-100 rounded-xl p-4 flex gap-3">
                <FaInfoCircle className="text-amber-500 mt-1 flex-shrink-0" />
                <p className="text-sm text-amber-800 leading-snug">
                  <span className="font-bold">Warning:</span> This action is
                  permanent and cannot be reversed.
                </p>
              </div>
            </div>

            <div className="flex gap-3 mt-8">
              <button
                className="flex-1 px-4 py-3 bg-[#f8f8f8] hover:bg-gray-200 text-[#17150b] text-base font-semibold rounded-[10px] transition-all"
                onClick={() => setIsDeleteModalOpen(false)}
              >
                Cancel
              </button>
              <button
                className="flex-1 px-4 py-3 bg-[#fef3f2] border border-[#ef4444] whitespace-nowrap text-[#ef4444] text-sm font-semibold rounded-[10px] transition-all flex items-center justify-center gap-2"
                disabled={deleteMutation.isLoading}
                onClick={() => {
                  deleteMutation.mutate(undefined, {
                    onSuccess: () => {
                      setIsDeleteModalOpen(false);
                      onOpenChange(false);
                    },
                  });
                }}
              >
                {deleteMutation.isLoading ? (
                  <FaSync className="animate-spin" />
                ) : (
                  <FaTrashAlt />
                )}
                Delete Permanently
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!trackerToResend} onOpenChange={(open) => !open && setTrackerToResend(null)}>
        <DialogContent
          className="max-w-md p-8 overflow-hidden border-none shadow-2xl"
          onClose={() => setTrackerToResend(null)}
        >
          <div className="p-0">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-amber-50 rounded-2xl flex items-center justify-center shrink-0">
                <FaExclamationTriangle className="text-amber-500 text-base" />
              </div>
              <div>
                <DialogTitle className="text-xl font-semibold text-[#17150b] leading-tight">
                  Resend Email?
                </DialogTitle>
              </div>
            </div>

            <div className="space-y-4">
              <p className="text-[#7d7b78] leading-relaxed">
                Are you sure you want to resend the campaign email to <span className="font-bold text-[#17150b]">{trackerToResend?.recipient_email}</span>?
              </p>
            </div>

            <div className="flex gap-3 mt-8">
              <button
                className="flex-1 px-4 py-3 bg-[#f8f8f8] hover:bg-gray-200 text-[#17150b] text-sm font-semibold rounded-[10px] transition-all"
                onClick={() => setTrackerToResend(null)}
              >
                Cancel
              </button>
              <button
                className="flex-1 px-4 py-3 bg-[#006EFF] text-white text-sm font-semibold rounded-[10px] transition-all flex items-center justify-center gap-2"
                onClick={confirmResend}
              >
                Resend Now
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Drawer>
  );
}
